import {
  Box,
  Paper,
  Typography,
  Grid,
  Autocomplete,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectUserData } from "../../../../../features/userSlice";
import {
  setSelectedSTOperationAccount,
  setSelectedSTOperationSharedUid,
} from "../../../../../features/scopesTabSlice";
import { useDispatch } from "react-redux";
import { useScopes } from "../STOperationhooks/useScopes";
import { useSearchParams } from "react-router-dom";
import { useSearchTermsTab } from "../STOperationhooks/useSearchTermsTab";

const SearchTermOperationCard = () => {
  const dispatch = useDispatch();
  const response = useSelector(selectUserData);
  const [searchParams, setSearchParams] = useSearchParams();
  // Redux states of Scopes Tab
  const {
    selectedSTOperationSharedUid,
    accountsForSTOperation,
    selectedSTOperationAccount,
    isScopesLoading,
    newScopeLoading,
    campaignToScopeLoading,
    editScopeLoading,
    removeNegativeLoadingForBothLoading,
    removeNegativeLoadingCampaignLoading,
  } = useSelector((state: any) => state.searchTermOperation);

  // Redux states of SearchTerm Tab
  const {
    searchTermTabLoader,
    confirmLoaderSTControl,
    confirmLoaderForContradictingSTDefinition,
  } = useSelector((state: any) => state.searchTermTab);
  const {
    clearParams,
    clearParamsForAccountSelection,
    clearParamsOfTables,
    accountsLoading,
    getAccountsBasedOnSite,
    fetchScopesTabData,
  } = useScopes();

  const { fetchSearchTermsTabData } = useSearchTermsTab();

  // Function to handle changes in site selection
  const handleSitesChange = (_: unknown, newValue: any) => {
    if (newValue?.shared_uid !== selectedSTOperationSharedUid) {
      clearParams(); // Clear parameters and related states
      if (newValue) {
        dispatch(setSelectedSTOperationSharedUid(newValue.shared_uid));
        getAccountsBasedOnSite(newValue.shared_uid); // Fetch accounts for the selected site
      } else {
        dispatch(setSelectedSTOperationSharedUid(""));
      }
    }
  };

  // Function to handle "GET DATA" button click
  const handleGetDataSTO = () => {
    clearParamsOfTables(); // Clear parameters related to tables
    //set url params
    const params = new URLSearchParams(searchParams);
    const currentTab = params.get("tab");

    params.set("selectedSite", selectedSTOperationSharedUid);
    params.set("account", selectedSTOperationAccount);
    params.set("clicked", "true");
    // Only set default tab "scopes" if tab is missing (first time)
    if (!currentTab) {
      params.set("tab", "SCOPES");
      //make api call
      fetchScopesTabData(
        selectedSTOperationSharedUid,
        selectedSTOperationAccount
      );
    } else {
      // Preserve current tab if user already has one selected
      params.set("tab", currentTab);
      if (currentTab === "SCOPES") {
        //make api call
        fetchScopesTabData(
          selectedSTOperationSharedUid,
          selectedSTOperationAccount
        );
      }
      if (currentTab === "SEARCH_TERMS") {
        fetchSearchTermsTabData();
      }
    }

    // Clean up unnecessary params
    ["filters", "page", "sort", "search", "accordion"].forEach((key) => {
      if (params.has(key)) params.delete(key);
    });
    setSearchParams(params, { replace: true });
  };

  //-- Get website list those have permisssions
  const siteOptions = useMemo(() => {
    return (
      response?.data?.website_permissions?.map((siteObj: any) => {
        const shared_uid = Object.keys(siteObj)[0]; // shared_uid of the site
        const site_name = siteObj[shared_uid].site_name; // Site name
        return { shared_uid, site_name }; // Return as an object with shared_uid and site_name
      }) || []
    );
  }, [response]);

  // Restore state from URL parameters on component mount (when reloaded page)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const website = params.get("selectedSite");
    const account = params.get("account");
    const clicked = params.get("clicked");
    const tab = params.get("tab");

    // Only proceed if we have the required URL parameters
    if (!website || !account || clicked !== "true") {
      return;
    }

    // Restore website and account selections
    dispatch(setSelectedSTOperationSharedUid(website));
    getAccountsBasedOnSite(website);
    dispatch(setSelectedSTOperationAccount(account));

    // Normalize tab value (default to SCOPES if missing)
    const normalizedTab = tab?.toUpperCase() || "SCOPES";

    // Fetch data based on the tab parameter
    switch (normalizedTab) {
      case "SCOPES":
        fetchScopesTabData(website, account);
        break;
      case "SEARCH_TERMS":
        fetchSearchTermsTabData();
        break;
      case "AI_RECOMMENDATIONS":
        // Add fetch function for AI recommendations when ready
        console.log("AI Recommendations tab - add fetch logic");
        break;
      default:
        // Default to SCOPES if unknown tab
        fetchScopesTabData(website, account);
    }
    // eslint-disable-next-line
  }, []);

  // Determine if any loader is active
  const isAnyLoaderTrue =
    isScopesLoading ||
    newScopeLoading ||
    campaignToScopeLoading ||
    editScopeLoading ||
    removeNegativeLoadingForBothLoading ||
    removeNegativeLoadingCampaignLoading ||
    searchTermTabLoader ||
    confirmLoaderSTControl ||
    confirmLoaderForContradictingSTDefinition;

  return (
    <Paper
      elevation={1}
      sx={{
        border: "1px solid",
        borderColor: "grey.300",
        padding: "8px 16px",
        borderRadius: 1,
        mb: 2,
      }}
    >
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={12}>
          <Box>
            <Typography variant="h5">Search Term Operations</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Maintain Clean Campaign Structures and Spot Account-Level Issues
              by Grouping Campaigns into Scopes and Surfacing Negative Search
              Term Anomalies.
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={1} alignItems="center" sx={{ mt: 1 }}>
        <Grid item xs={5.5} md={5.5}>
          <Autocomplete
            data-tracker="494"
            disablePortal
            sx={{ width: "100%" }}
            disabled={isAnyLoaderTrue}
            options={siteOptions}
            getOptionLabel={(option: any) => option?.site_name || ""}
            value={
              siteOptions?.find(
                (option: any) =>
                  option?.shared_uid === selectedSTOperationSharedUid
              ) || null
            }
            onChange={handleSitesChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Website"
                size="small"
                color="secondary"
                placeholder="Select a website from the list"
                required
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={5.5} md={5.5}>
          <Autocomplete
            data-tracker="495"
            disablePortal
            sx={{ width: "100%" }}
            disabled={
              !selectedSTOperationSharedUid ||
              accountsLoading ||
              isAnyLoaderTrue
            }
            options={accountsForSTOperation}
            getOptionLabel={(option: any) => option?.account_name || ""}
            value={
              accountsForSTOperation?.find(
                (option: any) =>
                  option?.account_shared_uid === selectedSTOperationAccount
              ) || null
            }
            onChange={(_, newValue) => {
              if (newValue?.account_shared_uid !== selectedSTOperationAccount) {
                clearParamsForAccountSelection(); // Clear parameters and related states
                if (newValue) {
                  dispatch(
                    setSelectedSTOperationAccount(newValue.account_shared_uid)
                  );
                } else {
                  dispatch(setSelectedSTOperationAccount(""));
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Account"
                size="small"
                color="secondary"
                placeholder={
                  accountsLoading
                    ? "Loading..."
                    : "Select an account from the list"
                }
                required
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {accountsLoading ? (
                        <CircularProgress color="secondary" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={1} md={1} sx={{ alignItems: "right", display: "flex" }}>
          <Button
            data-tracker="496"
            variant="contained"
            color="secondary"
            disabled={
              !selectedSTOperationSharedUid ||
              !selectedSTOperationAccount ||
              isAnyLoaderTrue
            }
            onClick={handleGetDataSTO}
            sx={{ minHeight: 42, ml: 2, width: "100%" }}
          >
            GET DATA
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SearchTermOperationCard;
